import { useMemo, useState, ReactNode } from 'react';
import { Button } from './button';
import { Input } from './input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './table';
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Plus,
  X,
  ChevronDown as ChevronDownIcon,
  Check,
  Download,
  SearchIcon,
} from 'lucide-react';

export interface FilterField {
  id: string;
  label: string;
  options: { value: string; label: string }[];
  value?: string;
}

export type DataTableColumn<Row> = {
  id?: string;
  key?: string;
  header: string;
  accessor?: (row: Row) => ReactNode;
  sortable?: boolean;
  sortValue?: (row: Row) => string | number;
  align?: 'left' | 'center' | 'right';
  className?: string;
  width?: string;
};

export type DataTableProps<Row> = {
  columns: DataTableColumn<Row>[];
  data: Row[];
  loading?: boolean;
  searchKeys?: (keyof Row | string)[];
  searchValue?: string;
  searchPlaceholder?: string;
  onSearchChange?: (value: string) => void;
  onSearchReset?: () => void;

  filterFields?: FilterField[];
  activeFilters?: { [key: string]: string };
  onFiltersChange?: (filters: { [key: string]: string }) => void;

  showColumnVisibility?: boolean;

  showAddButton?: boolean;
  addButtonText?: string;
  onAddClick?: () => void;
  showExportButton?: boolean;
  exportButtonText?: string;
  onExportClick?: () => void;

  initialPageSize?: number;
  pageSizeOptions?: number[];

  initialSortBy?: { id: string; desc: boolean };

  onRowClick?: (row: Row) => void;
  emptyText?: string;
};

const DEFAULT_PAGE_SIZES = [10, 25, 50];

function compareValues(a: string | number, b: string | number) {
  if (a === b) return 0;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export function DataTable<Row>({
  columns,
  data,
  searchKeys = [],
  searchValue: externalSearchValue,
  searchPlaceholder = 'Search records...',
  onSearchChange,
  onSearchReset,

  showColumnVisibility = false,

  showAddButton = false,
  addButtonText = 'Add',
  onAddClick,
  showExportButton = false,
  exportButtonText = 'Export',
  onExportClick,

  initialPageSize = 10,
  pageSizeOptions = DEFAULT_PAGE_SIZES,

  initialSortBy,

  onRowClick,
  emptyText = 'No data available',
}: DataTableProps<Row>) {
  const [internalSearchValue, setInternalSearchValue] = useState('');
  const searchValue =
    externalSearchValue !== undefined
      ? externalSearchValue
      : internalSearchValue;

  const [pageSize, setPageSize] = useState(initialPageSize);
  const [pageIndex, setPageIndex] = useState(0);
  const [sortBy, setSortBy] = useState<{ id: string; desc?: boolean } | null>(
    initialSortBy ? initialSortBy : null
  );
  const [showColumnDropdown, setShowColumnDropdown] = useState(false);
  const normalizedColumns = useMemo(() => {
    return columns.map(col => ({
      ...col,
      id: col.id || col.key || col.header.toLowerCase().replace(/\s+/g, '_'),
    }));
  }, [columns]);

  const [visibleColumns, setVisibleColumns] = useState<string[]>(
    normalizedColumns.map(col => col.id)
  );

  const handleSearchChange = (value: string) => {
    setInternalSearchValue(value);
    setPageIndex(0);
    if (onSearchChange) {
      onSearchChange(value);
    }
  };

  const handleSearchReset = () => {
    setInternalSearchValue('');
    setPageIndex(0);
    if (onSearchReset) {
      onSearchReset();
    }
  };

  const toggleColumnVisibility = (columnId: string) => {
    setVisibleColumns(prev =>
      prev.includes(columnId)
        ? prev.filter(id => id !== columnId)
        : [...prev, columnId]
    );
    setShowColumnDropdown(false);
  };

  const visibleColumnsData = useMemo(() => {
    return normalizedColumns.filter(col => visibleColumns.includes(col.id));
  }, [normalizedColumns, visibleColumns]);

  const filteredData = useMemo(() => {
    if (!searchValue || searchKeys.length === 0) return data;
    const term = searchValue.toLowerCase();
    return data.filter(row =>
      searchKeys.some(key => {
        const val = (row as any)[key];
        return (
          val !== undefined &&
          val !== null &&
          String(val).toLowerCase().includes(term)
        );
      })
    );
  }, [data, searchValue, searchKeys]);

  const sortedData = useMemo(() => {
    if (!sortBy) return filteredData;
    const column = normalizedColumns.find(c => c.id === sortBy.id);
    if (!column) return filteredData;

    const getVal = (row: Row) => {
      if (column.sortValue) return column.sortValue(row);
      const direct = (row as any)[column.id];
      if (direct !== undefined) return direct;
      if (column.key && (row as any)[column.key] !== undefined)
        return (row as any)[column.key];
      return '';
    };

    const sorted = [...filteredData].sort((a, b) => {
      const aValue = getVal(a);
      const bValue = getVal(b);
      const compare = compareValues(aValue, bValue);
      return sortBy.desc ? -compare : compare;
    });

    return sorted;
  }, [normalizedColumns, filteredData, sortBy]);

  const totalPages = Math.max(1, Math.ceil(sortedData.length / pageSize));

  const currentPageData = useMemo(() => {
    const start = pageIndex * pageSize;
    return sortedData.slice(start, start + pageSize);
  }, [sortedData, pageIndex, pageSize]);

  const handleSort = (col: (typeof normalizedColumns)[0]) => {
    if (!col.sortable) return;
    setPageIndex(0);

    setSortBy(prev => {
      if (!prev || prev.id !== col.id) {
        return { id: col.id, desc: false };
      }
      return { id: col.id, desc: !prev.desc };
    });
  };

  const handlePageChange = (nextIndex: number) => {
    setPageIndex(Math.max(0, Math.min(totalPages - 1, nextIndex)));
  };

  const handlePageSizeChange = (value: number) => {
    setPageSize(value);
    setPageIndex(0);
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div className="flex flex-col gap-1 w-full sm:w-auto">
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <Input
              type="text"
              value={searchValue}
              onChange={event => handleSearchChange(event.target.value)}
              placeholder={searchPlaceholder}
              className="h-10 w-full rounded-md border bg-background pl-9 pr-8 text-sm shadow-sm focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30 sm:w-80"
            />
            {searchValue && (
              <Button
                variant="ghost"
                size="sm"
                type="button"
                onClick={handleSearchReset}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
        <div className="flex space-x-2 w-full sm:w-auto items-center">
          {showColumnVisibility && (
            <div className="relative">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowColumnDropdown(!showColumnDropdown)}
                className="flex items-center gap-1"
              >
                Columns
                <ChevronDownIcon className="h-4 w-4" />
              </Button>
              {showColumnDropdown && (
                <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-slate-900 border border-gray-200 dark:border-slate-800 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
                  <div className="p-2">
                    {[...normalizedColumns].map(column => (
                      <div
                        key={column.id}
                        onClick={() => toggleColumnVisibility(column.id)}
                        className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-slate-800 rounded cursor-pointer"
                      >
                        <span className="text-sm text-gray-700 dark:text-gray-200">
                          {column.header}
                        </span>
                        {visibleColumns.includes(column.id) && (
                          <Check className="h-4 w-4 text-blue-600" />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
          {showAddButton && (
            <Button className="flex-1 sm:flex-none" onClick={onAddClick}>
              <Plus className="h-4 w-4 mr-2" />
              {addButtonText}
            </Button>
          )}
          {showExportButton && (
            <Button
              variant="outline"
              className="flex-1 sm:flex-none"
              onClick={onExportClick}
            >
              <Download className="h-4 w-4 mr-2" />
              {exportButtonText}
            </Button>
          )}
        </div>
      </div>

      <div className="border rounded-lg overflow-hidden bg-card shadow-sm">
        <div className="overflow-x-auto overflow-y-auto max-h-[500px]">
          <Table className="min-w-full">
            <TableHeader className="bg-muted/40">
              <TableRow>
                {visibleColumnsData.map(column => {
                  const isSorted = sortBy?.id === column.id;
                  const direction = isSorted && sortBy?.desc ? 'desc' : 'asc';
                  const alignClass =
                    column.align === 'right'
                      ? 'text-right'
                      : column.align === 'center'
                        ? 'text-center'
                        : 'text-left';

                  return (
                    <TableHead
                      key={column.id}
                      className={`${column.className || ''} ${alignClass} py-3 px-4 text-xs font-semibold uppercase tracking-wider text-muted-foreground whitespace-nowrap`}
                      style={
                        column.width
                          ? { width: column.width, minWidth: column.width }
                          : { minWidth: '100px' }
                      }
                    >
                      <button
                        type="button"
                        className={`inline-flex items-center gap-1.5 w-full ${column.align === 'right' ? 'justify-end' : column.align === 'center' ? 'justify-center' : 'justify-start'}`}
                        onClick={() => handleSort(column)}
                        disabled={!column.sortable}
                      >
                        {column.header}
                        {column.sortable ? (
                          <ChevronDown
                            className={
                              'h-3.5 w-3.5 transition-transform ' +
                              (isSorted
                                ? direction === 'asc'
                                  ? 'rotate-180 text-foreground font-bold'
                                  : 'text-foreground font-bold'
                                : 'opacity-40')
                            }
                          />
                        ) : null}
                      </button>
                    </TableHead>
                  );
                })}
              </TableRow>
            </TableHeader>
            <TableBody className="divide-y divide-border text-xs">
              {currentPageData.length > 0 ? (
                currentPageData.map((row, rowIndex) => (
                  <TableRow
                    key={(row as any).id || rowIndex}
                    className={
                      onRowClick
                        ? 'cursor-pointer hover:bg-muted/40 transition-colors'
                        : 'hover:bg-muted/20 transition-colors'
                    }
                    onClick={() => onRowClick?.(row)}
                  >
                    {visibleColumnsData.map(column => (
                      <TableCell
                        key={column.id}
                        className={`py-3 px-4 whitespace-nowrap ${column.align === 'right' ? 'text-right' : column.align === 'center' ? 'text-center' : 'text-left'}`}
                        style={
                          column.width
                            ? { width: column.width, minWidth: column.width }
                            : { minWidth: '100px' }
                        }
                      >
                        {column.accessor
                          ? column.accessor(row)
                          : (row as any)[column.id]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell
                    colSpan={visibleColumnsData.length}
                    className="text-center py-8 text-muted-foreground"
                  >
                    {emptyText}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between text-xs">
        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">Rows per page:</span>
          <select
            value={pageSize}
            onChange={e => handlePageSizeChange(Number(e.target.value))}
            className="rounded border border-border bg-background px-2 py-1 text-xs"
          >
            {pageSizeOptions.map(size => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </div>

        <div className="flex items-center gap-2">
          <span className="text-muted-foreground">
            Page {pageIndex + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-7 p-0"
            disabled={pageIndex === 0}
            onClick={() => handlePageChange(0)}
          >
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <Button
            variant="outline"
            size="sm"
            className="h-7 w-7 p-0"
            disabled={pageIndex >= totalPages - 1}
            onClick={() => handlePageChange(totalPages - 1)}
          >
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>
    </div>
  );
}
